// Mining Data Manager Hook - Simplified and robust data management

import { useEffect, useState, useCallback, useRef } from 'react';
import { useToast } from '../context/ToastContext';
import DataManager, { DataState } from '../services/dataManager';

export const useMiningDataManager = () => {
  const { showError, showWarning, showSuccess, showInfo } = useToast();
  const [dataState, setDataState] = useState<DataState>({
    dashboardStats: null,
    miners: [],
    cryptoProgress: null,
    averageHashrate: null,
    lastUpdate: 0,
    isLoading: true,
    isFetching: false,
    error: null,
    dataQuality: 'missing'
  });

  const dataManagerRef = useRef<DataManager | null>(null);

  // Initialize data manager
  useEffect(() => {
    console.log('🚀 Initializing Mining Data Manager...');

    const dataManager = new DataManager({
      onStateChange: (state) => {
        console.log('📊 Data state updated:', state);
        setDataState(state);
      },
      onError: (error, context) => {
        console.error(`❌ ${context}:`, error);
        showError('Connection Error', error);
      },
      onSuccess: (message) => {
        console.log(`✅ ${message}`);
        showSuccess('Success', message);
      },
      onWarning: (message) => {
        console.warn(`⚠️ ${message}`);
        showWarning('Warning', message);
      }
    });

    dataManagerRef.current = dataManager;
    dataManager.initialize();

    // Cleanup on unmount
    return () => {
      dataManager.destroy();
      dataManagerRef.current = null;
    };
  }, [showError, showWarning, showSuccess, showInfo]);

  // Manual refresh function
  const refreshData = useCallback(async () => {
    if (!dataManagerRef.current) return false;
    
    showInfo('Refreshing Data', 'Fetching latest mining information...');
    const success = await dataManagerRef.current.refresh();
    
    if (success) {
      showSuccess('Data Updated', 'Mining information refreshed successfully');
    }
    
    return success;
  }, [showInfo, showSuccess]);

  // Get data freshness info
  const getDataFreshness = useCallback(() => {
    return dataManagerRef.current?.getDataFreshness() || {
      isUsingCache: false,
      oldestDataAge: 0,
      hasStaleData: false,
      lastSuccessfulFetch: 0,
      dataQuality: 'missing' as const
    };
  }, []);

  // Check if can retry
  const canRetry = useCallback(() => {
    return dataManagerRef.current?.canRetry() || false;
  }, []);

  // Get time until retry
  const getTimeUntilRetry = useCallback(() => {
    return dataManagerRef.current?.getTimeUntilRetry() || 0;
  }, []);

  // Calculate derived statistics
  const derivedStats = {
    totalHashrateFormatted: dataState.dashboardStats 
      ? `${(dataState.dashboardStats.totalHashrate / 1000000000).toFixed(2)} GH/s`
      : '---',
    
    averageHashratePerWorker: dataState.dashboardStats && dataState.dashboardStats.totalWorkers > 0
      ? dataState.dashboardStats.totalHashrate / dataState.dashboardStats.totalWorkers
      : 0,
    
    onlineMinersCount: dataState.miners.filter(m => m.status === 'online').length,
    offlineMinersCount: dataState.miners.filter(m => m.status === 'offline').length,
    errorMinersCount: dataState.miners.filter(m => m.status === 'error').length,
    
    sha256Hashrate: dataState.miners
      .filter(m => m.algorithm === 'SHA256' && m.status === 'online')
      .reduce((sum, miner) => sum + miner.hashrate, 0),
    
    scryptHashrate: dataState.miners
      .filter(m => m.algorithm === 'Scrypt' && m.status === 'online')
      .reduce((sum, miner) => sum + miner.hashrate, 0),
    
    lastUpdateTime: dataState.lastUpdate
  };

  return {
    // Core data
    dashboardStats: dataState.dashboardStats,
    miners: dataState.miners,
    cryptoProgress: dataState.cryptoProgress,
    averageHashrate: dataState.averageHashrate,
    
    // State
    isLoading: dataState.isLoading,
    isFetching: dataState.isFetching,
    error: dataState.error,
    dataQuality: dataState.dataQuality,
    lastUpdate: dataState.lastUpdate,
    
    // Derived stats
    derivedStats,
    
    // Actions
    refreshData,
    
    // Status info
    dataFreshness: getDataFreshness(),
    canRetry: canRetry(),
    timeUntilRetry: getTimeUntilRetry(),
    
    // API status
    apiStatus: dataState.error ? 'error' : 
               dataState.dataQuality === 'fresh' ? 'connected' : 'cached',
    
    // Compatibility methods for existing components
    getCachedCryptoProgress: () => dataState.cryptoProgress,
    getCachedAverageHashrate: () => dataState.averageHashrate,
    
    // Settings (for compatibility)
    settings: {
      autoRefresh: true,
      refreshInterval: 30000,
      theme: 'dark' as const
    }
  };
};

export default useMiningDataManager;